/**
 * NIVARO UI — shared storefront behaviour for the NIVARO design layer.
 *
 * - NV.addToCart(variantId, quantity, sourceEl): adds through the AJAX cart and
 *   dispatches Shopify's standard CartLinesUpdateEvent, so Horizon's cart drawer,
 *   cart bubble and cart items re-render exactly as with the native product form.
 * - Quick add on product cards: [data-nv-quick-add] buttons. Single-variant
 *   products add directly; multi-variant products reveal [data-nv-size-picker].
 * - Favourites: [data-nv-wish="handle"] toggles, persisted per browser.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const WISH_KEY = 'nivaro:favoritos';

function readWishlist() {
  try {
    const raw = window.localStorage.getItem(WISH_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeWishlist(list) {
  try {
    window.localStorage.setItem(WISH_KEY, JSON.stringify(list));
  } catch {
    /* storage unavailable: favourites simply don't persist */
  }
  document.dispatchEvent(new CustomEvent('nv:wishlist:change', { detail: { handles: list } }));
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
}

async function addToCart(variantId, quantity = 1, sourceEl = document.body) {
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  sourceEl.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: sections.join(',') }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'No se pudo agregar');

    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'nivaro-quick-add',
        itemCount: quantity,
        sections: data.sections,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

window.NV = Object.assign(window.NV || {}, {
  addToCart,
  wishlist: {
    get: readWishlist,
    has: (handle) => readWishlist().includes(handle),
    toggle(handle) {
      const list = readWishlist();
      const index = list.indexOf(handle);
      if (index >= 0) list.splice(index, 1);
      else list.unshift(handle);
      writeWishlist(list);
      return index < 0;
    },
  },
});

/* ---------------------------------------------------------------- quick add */
document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const sizeButton = target.closest('[data-nv-add-variant]');
  const quickButton = target.closest('[data-nv-quick-add]');
  const closeButton = target.closest('[data-nv-size-picker-close]');

  if (closeButton) {
    closeButton.closest('[data-nv-size-picker]')?.setAttribute('hidden', '');
    return;
  }

  const button = sizeButton || quickButton;
  if (!button) return;

  const variantId = button.getAttribute('data-nv-add-variant');
  const card = button.closest('[data-nv-card]');

  if (quickButton && !variantId) {
    const picker = card?.querySelector('[data-nv-size-picker]');
    if (picker) {
      event.preventDefault();
      const willOpen = picker.hasAttribute('hidden');
      document.querySelectorAll('[data-nv-size-picker]:not([hidden])').forEach((el) => el.setAttribute('hidden', ''));
      if (willOpen) {
        picker.removeAttribute('hidden');
        picker.querySelector('button:not([disabled])')?.focus();
      }
    }
    return;
  }

  if (!variantId || button.hasAttribute('aria-busy')) return;
  event.preventDefault();
  button.setAttribute('aria-busy', 'true');
  button.classList.add('is-loading');
  try {
    await addToCart(variantId, 1, button);
    button.classList.add('is-added');
    card?.querySelector('[data-nv-size-picker]')?.setAttribute('hidden', '');
    setTimeout(() => button.classList.remove('is-added'), 1600);
  } catch (error) {
    button.classList.add('is-error');
    setTimeout(() => button.classList.remove('is-error'), 2000);
    console.warn('[nivaro] add to cart failed', error);
  } finally {
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
  }
});

/* ---------------------------------------------------------------- favourites */
function paintWishButtons(root = document) {
  const list = readWishlist();
  root.querySelectorAll('[data-nv-wish]').forEach((btn) => {
    const active = list.includes(btn.getAttribute('data-nv-wish'));
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-active', active);
  });
  document.querySelectorAll('[data-nv-wish-count]').forEach((el) => {
    // Only write when it changes: rewriting textContent is a childList mutation
    // that would retrigger the observer below every frame (starves idle callbacks).
    if (el.textContent !== String(list.length)) el.textContent = String(list.length);
    el.toggleAttribute('hidden', list.length === 0);
  });
}

document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-nv-wish]') : null;
  if (!btn) return;
  event.preventDefault();
  event.stopPropagation();
  window.NV.wishlist.toggle(btn.getAttribute('data-nv-wish'));
});

document.addEventListener('nv:wishlist:change', () => paintWishButtons());
document.addEventListener('DOMContentLoaded', () => paintWishButtons());
let paintQueued = false;
new MutationObserver(() => {
  if (paintQueued) return;
  paintQueued = true;
  requestAnimationFrame(() => {
    paintQueued = false;
    paintWishButtons();
  });
}).observe(document.body || document.documentElement, { childList: true, subtree: true });
paintWishButtons();

/* ------------------------------------------------- close pickers on outside */
document.addEventListener('pointerdown', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target?.closest('[data-nv-size-picker], [data-nv-quick-add]')) return;
  document.querySelectorAll('[data-nv-size-picker]:not([hidden])').forEach((el) => el.setAttribute('hidden', ''));
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('[data-nv-size-picker]:not([hidden])').forEach((el) => el.setAttribute('hidden', ''));
});
